import { useState } from "react"
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import * as XLSX from "xlsx"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"
import { useAuth } from "../../contexts/AuthContext"

function SecondaryJobsPage({ status = "processing" }) {
  const auth = useAuth()

  const [selectedJob, setSelectedJob] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [actionDialogOpen, setActionDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null) // "comment"
  const [comments, setComments] = useState("")
  const [jobDetailsForView, setJobDetailsForView] = useState(null)

  // Server-side pagination state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Exporting state
  const [isExporting, setIsExporting] = useState(false)

  const queryClient = useQueryClient()

  // Query parameter string for backend pagination and filtering
  const queryParams = new URLSearchParams()
  if (status) queryParams.set("status", status)
  queryParams.set("page", String(page + 1))
  queryParams.set("limit", String(rowsPerPage))

  const { data: result, isLoading } = useApiQuery(
    ["secondary-jobs", status, page, rowsPerPage],
    `/jobs/${auth?.user?._id}/assigned/secondary?${queryParams.toString()}`
  )

  const jobs = Array.isArray(result) ? result : (result?.jobs || [])
  const totalCount = Array.isArray(result) ? jobs.length : (result?.total ?? 0)

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "secondary-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  // Export to Excel handler
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true)
      const response = await api.get(
        `/jobs/${auth?.user?._id}/assigned/secondary?status=${status}&export=true`
      )
      const exportJobs = Array.isArray(response.data)
        ? response.data
        : (response.data?.jobs || [])

      if (!exportJobs || exportJobs.length === 0) {
        alert("No secondary jobs found to export.")
        return
      }

      const excelRows = exportJobs.map((job) => {
        const secondaryUserNames =
          Array.isArray(job.secondaryUser) && job.secondaryUser.length > 0
            ? job.secondaryUser.map((u) => u.name || u.phoneNumber || "User").join(", ")
            : "None"

        const commentsFormatted =
          Array.isArray(job.comments) && job.comments.length > 0
            ? job.comments
                .map((c) => {
                  const dateStr = c.commentedAt ? new Date(c.commentedAt).toLocaleString("en-IN") : ""
                  const author = c.commentedBy?.name || "Unknown"
                  return `[${dateStr} by ${author}]: ${c.commentText || ""}`
                })
                .join("\n")
            : "No comments"

        return {
          "Job ID": job.JobId || job.jobId || "N/A",
          "Client Name": job.clientName || "N/A",
          "Client PAN": job.clientPan || "N/A",
          "Client Phone": job.clientPhoneNumber || "N/A",
          "Job Type": job.jobType || "N/A",
          "Due Date": job.dueDate ? new Date(job.dueDate).toLocaleDateString("en-IN") : "N/A",
          "Status": job.status || "N/A",
          "Sub Status": job.subStatus || "None",
          "Assigned To": job.assignedToName || "Unassigned",
          "Secondary Users": secondaryUserNames,
          "Fees": job.fees ?? 0,
          "Fee Status": job.feeStatus || "pending",
          "Comments": commentsFormatted,
          "Created At": job.createdAt ? new Date(job.createdAt).toLocaleString("en-IN") : "N/A",
        }
      })

      const statusLabel = status === "new" ? "New" : status === "completed" ? "Completed" : "Processing"
      const worksheet = XLSX.utils.json_to_sheet(excelRows)
      const workbook = XLSX.utils.book_new()
      const sheetTitle = `Secondary ${statusLabel}`
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle)

      const todayStr = new Date().toISOString().split("T")[0]
      const fileName = `Secondary_Jobs_${status}_${todayStr}.xlsx`
      XLSX.writeFile(workbook, fileName)
    } catch (error) {
      console.error("Failed to export secondary jobs:", error)
      alert("Failed to export jobs. Please try again.")
    } finally {
      setIsExporting(false)
    }
  }

  // Define table columns
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { id: "client", label: "Client", field: "clientName" },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    { id: "subStatus", label: "Sub Status", field: "subStatus", type: "subStatus" },
  ]

  // Define actions menu items
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "addComment":
        setDialogType("comment")
        setActionDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false)
    setDialogType(null)
    setComments("")
    setSelectedJob(null)
  }

  const handleConfirmUpdate = () => {
    if (dialogType === "comment" && comments) {
      setJobCommentMutation.mutate({ commentText: comments })
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const statusLabel = status === "new" ? "New" : status === "completed" ? "Completed" : "Processing"

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Secondary Jobs ({statusLabel})
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Jobs where you are assigned as secondary and the status is {statusLabel}.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          onClick={handleExportToExcel}
          disabled={isExporting || isLoading || jobs.length === 0}
        >
          {isExporting ? "Exporting..." : "Export to Excel"}
        </Button>
      </Box>

      {/* Jobs Table with Reusable Component */}
      <JobsTable
        jobs={jobs}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No secondary jobs found in this status"
        serverSidePagination={true}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* View Details Dialog */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
        }}
        job={jobDetailsForView}
      />

      {/* Action Dialog (Add Comment) */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add notes about the update..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            disabled={!comments}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SecondaryJobsPage